import { useTranslator } from '@translator-app/react';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';

import { useApplicationState } from '../../contexts/application-state-context';
import { useKioskMode } from '../../contexts/kiosk-context';
import { ShopObjectType } from '../../generated/graphql';
import MapPinIcon from '../../icons/map-pin.svg';
import PhoneIcon from '../../icons/phone.svg';
import { formatPhonenumber } from '../../utils/phonenumber';
import { groupReservations, isReservationAvailable } from '../../utils/reservation-grouping';
import { Image } from '../atoms/image';
import { SiteNav } from './site-nav';

interface Props {
  shop?: ShopObjectType;
}

export const ShopHeader: React.FC<Props> = (props) => {
  const { shop } = props;
  const router = useRouter();
  const { shoppingCart, organisation, reservations } = useApplicationState();
  const { isKioskMode } = useKioskMode();
  const { translate } = useTranslator();

  const rootLinkHref = !organisation
    ? '/'
    : shop
    ? `/${organisation.pathname}/${shop._id}`
    : `/${organisation.pathname}`;

  const isShopIndex = router.pathname === '/[organisation]/[shopId]';
  const isOrderStatusPage =
    router.pathname === '/order/[shortToken]' || router.pathname === '/[organisation]/[shopId]/order/[shortToken]';
  const showShopHero = Boolean(shop && !isKioskMode && isShopIndex);

  const activeOffersCount = useMemo(() => {
    if (!showShopHero) {
      return 0;
    }

    return groupReservations(reservations.filter(isReservationAvailable)).length;
  }, [reservations, showShopHero]);

  const addressLine = shop
    ? `${shop.address.street} ${shop.address.streetNumber}, ${shop.address.zipcode} ${shop.address.city}`
    : '';

  return (
    <>
      <SiteNav
        logoHref={rootLinkHref}
        cartHref={
          shop && organisation
            ? `/${router.locale}/${organisation.pathname}/${shop._id}/order/shopping-cart`
            : undefined
        }
        cartCount={shoppingCart.length}
        showCart={!isOrderStatusPage}
      />

      {showShopHero && shop && (
        <section className="mx-auto max-w-7xl px-6 py-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
            {organisation?.logoUrl ? (
              <Image
                url={organisation.logoUrl}
                alt={`${shop.name} logo`}
                width={144}
                height={144}
                sizes="144px"
                className="object-contain object-left max-h-24 max-w-48 md:max-h-36 md:max-w-36 md:shrink-0 "
              />
            ) : null}

            <div className="flex-1">
              <h1 className="font-display-bold text-4xl leading-none tracking-tight text-dark md:text-5xl">
                {shop.name}
              </h1>
              <p className="mt-3 flex items-center gap-2 text-base text-muted-foreground">
                <MapPinIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                {addressLine}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {shop.phonenumber && (
                  <a
                    href={`tel:${shop.phonenumber}`}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-primary hover:text-primary"
                  >
                    <PhoneIcon className="h-4 w-4" aria-hidden />
                    {formatPhonenumber(shop.phonenumber)}
                  </a>
                )}
              </div>
              <p className="mt-4 text-base text-muted-foreground">
                {shop.openingHours ||
                  translate('overview.pickupOrderWithin', {
                    vendingPickupTime: shop.vendingPickupTime.toString(10),
                  })}
              </p>
            </div>

            {activeOffersCount > 0 && (
              <div className="hidden md:blockrounded-2xl border border-primary/20 bg-primary-light px-5 py-4 text-center md:self-center">
                <div className="font-display-bold text-3xl text-primary-dark">{activeOffersCount}</div>
                <div className="text-xs font-medium uppercase tracking-wide text-primary-dark">
                  {translate('shopHeader.activeOffers')}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
};
